/**
 * Сервис для синхронизации очереди запросов с сервером
 */
import { api } from './api';
import { localStorageService } from './localStorage';
import { onlineStatusService } from './offline';

interface SyncOptions {
  onSuccess?: (requestId: string) => void;
  onError?: (requestId: string, error: any) => void;
  onProgress?: (synced: number, total: number) => void;
}

class SyncQueueService {
  private isSyncing: boolean = false;
  private syncListeners: Set<(isSyncing: boolean) => void> = new Set();

  constructor() {
    // Подписываемся на изменения статуса онлайн
    onlineStatusService.subscribe((isOnline) => {
      if (isOnline && !this.isSyncing) {
        // Автоматически запускаем синхронизацию при появлении интернета
        this.sync();
      }
    });

    // Периодическая синхронизация (каждые 30 секунд, если онлайн)
    setInterval(() => {
      if (onlineStatusService.getStatus() && !this.isSyncing) {
        this.sync();
      }
    }, 30000);
  }

  public subscribe(callback: (isSyncing: boolean) => void): () => void {
    this.syncListeners.add(callback);
    callback(this.isSyncing);
    return () => {
      this.syncListeners.delete(callback);
    };
  }

  private notifyListeners(isSyncing: boolean) {
    this.syncListeners.forEach((listener) => listener(isSyncing));
  }

  public async sync(options: SyncOptions = {}): Promise<void> {
    if (this.isSyncing) {
      return;
    }

    if (!onlineStatusService.getStatus()) {
      return;
    }

    this.isSyncing = true;
    this.notifyListeners(true);

    try {
      const pendingRequests = await localStorageService.getPendingRequests();
      const total = pendingRequests.length;

      if (total === 0) {
        this.isSyncing = false;
        this.notifyListeners(false);
        return;
      }

      let synced = 0;

      for (const request of pendingRequests) {
        try {
          // Выполняем запрос
          const config: any = {
            method: request.method as any,
            url: request.url,
            headers: request.headers,
          };

          if (request.method !== 'GET' && request.data) {
            config.data = request.data;
          }

          await api.request(config);

          // Удаляем успешно выполненный запрос
          await localStorageService.removePendingRequest(request.id);
          synced++;

          options.onSuccess?.(request.id);
          options.onProgress?.(synced, total);
        } catch (error: any) {
          // Если ошибка 4xx (кроме 401), удаляем запрос (не будем повторять)
          if (error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 401) {
            await localStorageService.removePendingRequest(request.id);
            options.onError?.(request.id, error);
            continue;
          }

          // Увеличиваем счетчик попыток
          const retries = request.retries + 1;
          await localStorageService.updatePendingRequest(request.id, { retries });

          // Если слишком много попыток, удаляем запрос
          if (retries >= 5) {
            await localStorageService.removePendingRequest(request.id);
            options.onError?.(request.id, error);
          } else {
            options.onError?.(request.id, error);
          }
        }
      }
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      this.isSyncing = false;
      this.notifyListeners(false);
    }
  }

  public async getPendingCount(): Promise<number> {
    const requests = await localStorageService.getPendingRequests();
    return requests.length;
  }
}

export const syncQueueService = new SyncQueueService();

